// Local persistent cache for large, effectively-immutable AAM binary assets
// (the /ogs decoders geom/appr/mesh + hair.gsh). These come back
// `Cache-Control: public, max-age=86400` but the browser HTTP disk cache is
// unreliable here — cross-origin under automation, and Vite-proxied in staging —
// so we cache them ourselves in the Cache Storage API, which is verifiable,
// survives reloads + sessions, and is independent of any proxy header behaviour.
//
// Freshness (CLAUDE.md — server is the source of truth): the cache key embeds the
// file's `size` from its /ogs list row, so a re-upload that changes the bytes
// changes the size → a new key → a cache MISS → a fresh download. A same-size
// re-upload is the only stale case (rare for inference blobs); the server can
// still force-refresh sooner by shipping an ETag / shorter max-age (none today).
// A cache HIT resolves with NO network request at all.
//
// That row `size` versions the key well, but it is NOT the exact byte count — in
// practice the AAM reports less than it serves (see reportOversizeRow below). So it
// is used as a LOWER BOUND: short of it means a truncated download, over it means
// the metadata understates the file. Only the former refuses the cache.

import { assetLoadProgress } from './assetLoadProgress.js'
import { cacheFirst } from './cacheFirst.js'

const DEFAULT_CACHE_NAME = 'aam-file-v1'

// Friendly download-progress label for the boot bar, from an /ogs file path.
export function labelForAam(path) {
  const name = String(path).split('?')[0].split('/').pop() || 'file'
  if (/^(geom|appr|mesh|trunk|rig2mesh)/i.test(name)) return 'Downloading avatar model…'
  if (/hair\.gsh$/i.test(name)) return 'Downloading hair…'
  return `Downloading ${name}…`
}

// Drop the whole AAM-file cache bucket (the /ogs decoders + hair.gsh). Wired
// into the "Clear Cache" recovery button so a stale/corrupt cached asset can't
// survive a clear and keep the avatar broken. Best-effort, never throws.
export async function clearAamFileCache() {
  try { if (typeof caches !== 'undefined') await caches.delete(DEFAULT_CACHE_NAME) } catch { /* non-fatal */ }
}

// A file that arrived BIGGER than its /ogs row claims. NOT a warning, deliberately:
// there is nothing wrong with the download (a truncation cannot add bytes) and
// nothing the client can do about the row. The AAM understates `size` for every /ogs
// file checked — eyeline-v10c's export_manifest.json declares 940, which is exactly
// the `Content-Encoding: br` Content-Length of the 3132-byte object it serves, and
// isaac-v6's rows are low by the same kind of margin — so the row is a LOWER BOUND,
// not the exact byte count this layer once assumed. Warning about it would fire for
// every file of every bundle, on every load, about a server-side metadata detail
// (fixable only in the asset-manager) that costs the user nothing now that the body
// is cached anyway. console.debug keeps it available under devtools' Verbose level
// for anyone chasing sizes, and out of the shell's error/warning tally.
function reportOversizeRow(path, got, want) {
  console.debug(
    `[aam-cache] ${path}: ${got} bytes, /ogs row declares ${want} — the row understates it ` +
    `(compressed/stale metadata). Cached anyway; only SHORT bodies fail verification.`)
}

// Cache key for a path + size. Kept pure + exported so it's unit-testable.
// size undefined → key by path alone (cache-first, no size-versioning).
export function assetCacheKey(path, size) {
  if (size == null) return path
  const sep = path.includes('?') ? '&' : '?'
  return `${path}${sep}__aamv=${size}`
}

// Cache-first fetch through the Cache Storage API. On miss, fetches via
// `fetchImpl(path)` and stores an ok response under the size-versioned key. On a
// context without Cache Storage (insecure origin / SSR / a store that won't open)
// it transparently falls through to `fetchImpl` — never a silent failure, just no
// caching. `cachesImpl` is injectable for tests; defaults to the global `caches`.
export async function cachedFetch(path, size, { fetchImpl, cachesImpl, cacheName = DEFAULT_CACHE_NAME, meter = false } = {}) {
  const store = cachesImpl ?? (typeof caches !== 'undefined' ? caches : null)
  if (!store) return fetchImpl(path)

  let cache
  try {
    cache = await store.open(cacheName)
  } catch {
    return fetchImpl(path) // private mode / storage disabled → no cache, still fetch
  }

  const key = assetCacheKey(path, size)
  const res = await cacheFirst(cache, key, () => fetchImpl(path), {
    id: path,
    label: labelForAam(path),
    sizeOf: () => size || 0,
    meter,
    // The /ogs list row states each file's exact byte count, so we can check the
    // one thing that used to fail silently. A short body reached the decoders as
    // a cryptic RangeError 500 MB into the load; caught here it names the file
    // and the shortfall, and — crucially — is NOT cached, so the next attempt
    // re-downloads instead of replaying the same broken bytes.
    verifyBytes: size || 0,
    onShort: (got, want) => {
      console.error(
        `[aam-cache] ${path} downloaded short: ${got} of ${want} bytes ` +
        `(short by ${want - got}). Not caching it; the next load will re-fetch.`)
    },
    onOversize: (got, want) => reportOversizeRow(path, got, want),
  })
  if (meter && res && !res.ok) {
    // Non-ok response: the caller throws on !res.ok (loud, surfaced by the App
    // error overlay), but tell the progress bar too so it doesn't sit forever
    // waiting on a task that will never complete.
    assetLoadProgress.register(path, labelForAam(path), size || 0)
    assetLoadProgress.fail(path)
  }
  return res
}
